import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertDialog,
  Body,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuItem,
  I,
  PageHeader,
  PageHeaderSkeleton,
  Pill,
  Row,
  SearchInput,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { Tooltip } from '@leanin/ui/client';
import { useMessageMember } from '../../groups/use-message-member';
import { InviteMemberModal } from '../../group/InviteMemberModal';
import { GroupPendingInvitesCard } from '../../group/admin/GroupPendingInvitesCard';
import { CopyJoinLinkButton } from '../../group/admin/CopyJoinLinkButton';
import type { GroupMemberView } from '../../../lib/views/groups';
import { useCircle, useInfiniteGroupMembers } from '../../../lib/queries/groups';
import {
  useLeaveGroup,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from '../../../lib/mutations/groups';
import { useGroupLeaveFlow } from '../../group/use-group-leave-flow';
import { formatGroupMemberError } from '../../groups/format-group-member-error';
import { useConfirmDialog } from '../../../lib/use-confirm-dialog';
import {
  MembersTableSkeleton,
  buildMemberColumns,
  filterMembersBySearch,
  memberName,
} from '../../group/admin/members-table';

type Props = {
  readonly circleId: string;
  readonly viewerUserId: string;
};

export function CircleMembersSection({ circleId, viewerUserId }: Props) {
  const t = useTranslations('Group');
  const tc = useTranslations('Circles');
  const detailQuery = useCircle(circleId);
  const membersQuery = useInfiniteGroupMembers(circleId, { pageSize: 100 });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = membersQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const navigate = useNavigate();
  const setRoleMutation = useSetGroupMemberRole();
  const removeMutation = useRemoveGroupMember();
  const leaveMutation = useLeaveGroup();
  const { messageMember, error: messageError } = useMessageMember();

  const leaveFlow = useGroupLeaveFlow({
    leave: (handlers) =>
      leaveMutation.mutate(
        { id: circleId, kind: 'circle' },
        {
          onSuccess: () => {
            // Land back on /circles since the viewer is no longer a member.
            navigate({ to: '/circles' });
            handlers.onSuccess();
          },
          onError: handlers.onError,
        },
      ),
    fallbackMessage: tc('membersAdmin.errorLeave'),
  });

  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const remove = useConfirmDialog<GroupMemberView>();
  const demote = useConfirmDialog<GroupMemberView>();

  const isPending =
    setRoleMutation.isPending ||
    removeMutation.isPending;

  const handlePromote = (m: GroupMemberView) => {
    setError(null);
    setRoleMutation.mutate(
      { groupId: circleId, kind: 'circle', userId: m.userId, role: 'leader' },
      {
        onError: (err) =>
          setError(formatGroupMemberError(err, tc('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmDemote = () => {
    if (demote.target === null) return;
    const target = demote.target;
    setError(null);
    setRoleMutation.mutate(
      { groupId: circleId, kind: 'circle', userId: target.userId, role: 'member' },
      {
        onSettled: () => demote.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, tc('membersAdmin.errorSetRole'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const confirmRemove = () => {
    if (remove.target === null) return;
    const target = remove.target;
    setError(null);
    removeMutation.mutate(
      { groupId: circleId, userId: target.userId },
      {
        onSettled: () => remove.close(),
        onError: (err) =>
          setError(formatGroupMemberError(err, tc('membersAdmin.errorRemove'), t('membersAdmin.lastLeaderNudge'))),
      },
    );
  };

  const visibleError = error ?? messageError;

  const showSkeleton = useDelayedLoading(membersQuery.data === undefined);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton />
        <MembersTableSkeleton rows={6} />
      </Stack>
    );
  }
  if (membersQuery.data === undefined) return null;

  const allMembers = membersQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const members = filterMembersBySearch(allMembers, search);

  // Leave-button gating:
  //   * If the viewer isn't on this circle's roster (parent-network-leader
  //     viewing a child circle via the admin cascade), hide the button
  //     entirely — they can't leave a circle they were never on.
  //   * If they're the sole leader, the button shows disabled with a
  //     tooltip prompting them to promote someone first.
  //   * Otherwise the button is enabled.
  const viewerMembership = allMembers.find((m) => m.userId === viewerUserId);
  const viewerIsOnRoster = viewerMembership !== undefined;
  const leaderCount = allMembers.filter((m) => m.role === 'leader').length;
  const viewerIsOnlyLeader =
    viewerMembership?.role === 'leader' && leaderCount <= 1;

  // Flat leadership: every `role='leader'` row is equivalent. The pill
  // reads "Leader" / "Member" with no hierarchy. Server-side guards refuse
  // the destructive operations that would leave the circle leaderless;
  // the dropdown surfaces them all and falls back to a banner on error.
  const columns = buildMemberColumns<GroupMemberView>({
    viewerUserId,
    t,
    renderRole: (m) => {
      const isLeader = m.role === 'leader';
      return (
        <Pill tone={isLeader ? 'burgundy' : 'neutral'}>
          {isLeader ? tc('membersAdmin.roleLeader') : tc('membersAdmin.roleMember')}
        </Pill>
      );
    },
    renderActions: (m, { name }) => {
      const isLeader = m.role === 'leader';
      return (
        <Row gap="xs" align="center" justify="flex-end">
          <Button
            variant="ghost"
            size="sm"
            icon={I.msg}
            aria-label={tc('membersAdmin.messageMemberAria', { name })}
            onClick={() => messageMember(m.userId)}
          />
          <DropdownMenu
            align="end"
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={I.more}
                aria-label={tc('membersAdmin.manageMemberAria', { name })}
              />
            }
          >
            {!isLeader ? (
              <DropdownMenuItem
                onSelect={() => handlePromote(m)}
                disabled={isPending}
              >
                {tc('membersAdmin.promote')}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onSelect={() => demote.request(m)}
                disabled={isPending}
              >
                {tc('membersAdmin.demote')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              destructive
              disabled={isPending}
              onSelect={() => remove.request(m)}
            >
              {tc('membersAdmin.removeFromCircle')}
            </DropdownMenuItem>
          </DropdownMenu>
        </Row>
      );
    },
  });

  const circleName =
    detailQuery.data && detailQuery.data.access !== 'visitor'
      ? detailQuery.data.circle.name
      : '';

  return (
    <Stack gap="lg">
      <PageHeader
        title={tc('membersAdmin.heading')}
        subtitle={tc('membersAdmin.subtitle')}
        actions={
          <Row gap="sm" align="center">
            {!viewerIsOnRoster ? null : viewerIsOnlyLeader ? (
              <Tooltip content={tc('membersAdmin.onlyLeaderTooltip')}>
                {/* Span so the Tooltip's `asChild` trigger can hang focus +
                 *  hover handlers on a wrapper — Radix discards the events
                 *  on a disabled <button> child. */}
                <span tabIndex={0}>
                  <Button variant="secondary" size="md" disabled>
                    {tc('membersAdmin.leaveCircle')}
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="secondary"
                size="md"
                onClick={leaveFlow.openConfirm}
              >
                {tc('membersAdmin.leaveCircle')}
              </Button>
            )}
            <CopyJoinLinkButton groupId={circleId} />
            <Button
              variant="primary"
              size="md"
              icon={I.plus}
              onClick={() => setInviteOpen(true)}
            >
              {tc('membersAdmin.addMember')}
            </Button>
          </Row>
        }
      />

      <Stack gap="md">
        <SearchInput
          placeholder={tc('membersAdmin.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <DataTable
          key={search}
          data={[...members]}
          columns={columns}
          pagination={{ pageSize: 10 }}
        >
          <DataTable.Header />
          <DataTable.Body emptyMessage={tc('membersAdmin.emptyTable')} />
          <DataTable.Pagination />
        </DataTable>
        <Row justify="space-between" align="center">
          <Body size="sm" color="mute">
            {tc('membersAdmin.countSummary', {
              count: members.length,
              total: detailQuery.data?.circle.memberCount ?? allMembers.length,
            })}
          </Body>
          <Body size="sm" color="mute">
            {tc('membersAdmin.promoteHint')}
          </Body>
        </Row>
      </Stack>

      {visibleError !== null ? (
        <span role="alert" aria-live="polite">
          <Body size="sm" color="destructive">{visibleError}</Body>
        </span>
      ) : null}

      <GroupPendingInvitesCard kind="circle" groupId={circleId} />

      <InviteMemberModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        kind="circle"
        groupId={circleId}
        groupName={circleName}
      />

      <AlertDialog
        open={remove.open}
        onOpenChange={remove.onOpenChange}
        title={
          remove.target !== null
            ? tc('membersAdmin.removeDialogTitle', { name: memberName(remove.target) })
            : tc('membersAdmin.removeDialogTitleFallback')
        }
        description={
          remove.target !== null
            ? tc('membersAdmin.removeDialogBody', { name: memberName(remove.target) })
            : ''
        }
        actionLabel={tc('membersAdmin.removeDialogConfirm')}
        cancelLabel={tc('membersAdmin.removeDialogCancel')}
        destructive
        onAction={confirmRemove}
      />

      <AlertDialog
        open={demote.open}
        onOpenChange={demote.onOpenChange}
        title={
          demote.target !== null
            ? tc('membersAdmin.demoteDialogTitle', { name: memberName(demote.target) })
            : tc('membersAdmin.demoteDialogTitleFallback')
        }
        description={tc('membersAdmin.demoteDialogBody')}
        actionLabel={tc('membersAdmin.demoteDialogConfirm')}
        cancelLabel={tc('membersAdmin.demoteDialogCancel')}
        destructive
        onAction={confirmDemote}
      />

      <AlertDialog
        open={leaveFlow.confirmOpen}
        onOpenChange={leaveFlow.setConfirmOpen}
        title={tc('membersAdmin.leaveDialogTitle')}
        description={tc('membersAdmin.leaveDialogBody')}
        actionLabel={tc('membersAdmin.leaveCircle')}
        cancelLabel={tc('membersAdmin.leaveDialogCancel')}
        destructive
        onAction={leaveFlow.confirmLeave}
      />
      {leaveFlow.error !== null ? (
        <Body size="sm" color="destructive">{leaveFlow.error}</Body>
      ) : null}

    </Stack>
  );
}
